// @flow

import { mapTags, type TranslatorProps } from '@gandi/translate';

import {
  togglePillGroupRenderer,
  multiSelectRenderer,
  switchRenderer,
  showSelectedPillGroupRenderer,
} from './ContactForm';
import type { FieldSchema } from './ContactForm';

// Describes the contact object in the contactbook API.
// The elements are looked up using the name field, make sure that they are unique within this list.
export const getContactFields: (
  translator: TranslatorProps,
  isEdition?: boolean,
) => Array<FieldSchema> = ({ __ }, isEdition) => [
  {
    name: 'id',
    type: 'string',
    label: __('id'),
    required: true,
  },
  {
    name: 'roles',
    type: 'someOf',
    label: __(
      'Set contact and roles for next domain purchases with this organization',
    ),
    values: [
      {
        value: 'admin',
        text: __('Administrative'),
      },
      {
        value: 'tech',
        text: __('Technical'),
      },
      {
        value: 'bill',
        text: __('Billing'),
      },
    ],
    renderer: multiSelectRenderer,
  },
  {
    name: 'type',
    type: 'oneOf',
    required: true,
    label: __('Type'),
    values: [
      {
        value: 'company',
        text: __('Company'),
      },
      {
        value: 'publicbody',
        text: __('Public Body'),
      },
      {
        value: 'association',
        text: __('Association'),
      },
      {
        value: 'individual',
        text: __('Individual'),
      },
    ],
    renderer: isEdition
      ? showSelectedPillGroupRenderer
      : togglePillGroupRenderer,
  },
  {
    name: 'orgname',
    type: 'string',
    label: __('Organization name'),
  },
  {
    name: 'given',
    type: 'string',
    label: __('Given name'),
    required: true,
  },
  {
    name: 'family',
    type: 'string',
    label: __('Family name'),
    required: true,
  },
  {
    name: 'country',
    type: 'country',
    label: __('Country'),
  },
  {
    name: 'streetaddr',
    type: 'string',
    label: __('Street address'),
  },
  {
    name: 'city',
    type: 'string',
    label: __('City'),
  },
  {
    name: 'state',
    type: 'state',
    label: __('State'),
  },
  {
    name: 'zip',
    type: 'zip',
    label: __('Zip code'),
  },
  {
    name: 'email',
    type: 'email',
    label: __('Email'),
    required: true,
  },
  {
    name: 'phone',
    type: 'phone',
    label: __('Phone number'),
    required: true,
  },
  {
    name: 'fax',
    type: 'phone',
    label: __('Fax number'),
  },
  {
    name: 'mail_obfuscated',
    type: 'boolean',
    label: __('Email address privacy'),
    sublabel: __(
      "Hide email address in Gandi's public WHOIS database and at the registry.",
    ),
    description: mapTags(
      __(
        'We take care of forwarding email to your address. ' +
          '[[whoisPrivacyLink:Learn more]].\n' +
          'If you deactivate the option, you agree to your real email ' +
          'address be stored in the registry database.',
      ),
      ({ whoisPrivacyLink }) => ({
        whoisPrivacyLink: (
          <a
            href={__(
              'https://docs.gandi.net/en/domain_names/common_operations/whois_privacy.html',
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            {whoisPrivacyLink}
          </a>
        ),
      }),
    ),
    renderer: switchRenderer,
  },
  {
    name: 'data_obfuscated',
    type: 'boolean',
    label: __('Personal data privacy'),
    sublabel: __("Hide personnal data in Gandi's public WHOIS database."),
    description: mapTags(
      __(
        'Some extensions protect data at the registry level. ' +
          '[[tldListLink:See which ones]].\n' +
          'If you deactivate the option, you agree to make all your data ' +
          ' public. [[whoisPrivacyLink:Learn more]].',
      ),
      ({ tldListLink, whoisPrivacyLink }) => ({
        tldListLink: (
          <a
            href={__(
              'https://docs.gandi.net/en/domain_names/common_operations/whois_privacy.html',
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            {tldListLink}
          </a>
        ),
        whoisPrivacyLink: (
          <a
            href={__(
              'https://docs.gandi.net/en/domain_names/common_operations/whois_privacy.html',
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            {whoisPrivacyLink}
          </a>
        ),
      }),
    ),
    renderer: switchRenderer,
  },
];

// **********************************************

// Describes how the form will be displayed

export const formSection1 = {
  name: 'form__section1',
  type: 'form__section',
  children: [{ name: 'roles' }],
};

export const formSection2 = {
  name: 'form__section2',
  type: 'form__section',
  children: [
    { name: 'type' },
    { name: 'orgname' },
    { name: 'given' },
    { name: 'family' },
    {
      name: 'address',
      type: 'address',
    },
    { name: 'email' },
    { name: 'phone' },
    { name: 'fax' },
    { name: 'mail_obfuscated' },
    { name: 'data_obfuscated' },
  ],
};
